import { FOOD_NAMESPACE, FOOD_SCHEMA, KEEP_BUDGETS, foodEnvironmentReady, foodKey, pruneFoldedResults } from '../actions/food';

/**
 * Writes the food store's `confirmed` to the browser's copy, 300 ms after it last changed, so a
 * reload, or a page opened offline, starts from it (docs/local-first-food-data.md, phase 2).
 *
 * Only once a snapshot has arrived (status `fresh`): a restored copy is already stored, and changes
 * merged into one are written with the snapshot that follows. A write lands only while this tab's
 * session marker is the stored one and its version is not older than the stored copy's (see
 * persistedStore.js), and each keeps the user's KEEP_BUDGETS most recently saved budgets.
 *
 * Once a copy holding a snapshot is written, the op results folded before that snapshot are deleted
 * from the browser (pruneFoldedResults): until then a reload rebuilds what they changed from them.
 * The copy also names the applied results it holds, so a reload does not fold those over it again,
 * undoing anything written after them.
 *
 * Returns `{ stop, flush }`: `flush` writes now instead of waiting, and resolves whether it wrote.
 */
export function persistFood(store, {
	delay = 300,
	schedule = (callback, ms) => setTimeout(callback, ms),
	cancel = (timer) => clearTimeout(timer),
	environment = foodEnvironmentReady,
} = {}) {
	let lastConfirmed = store.getState().food.confirmed;
	let timer = null;
	let stopped = false;

	const persistable = (food) => !stopped && food.status === 'fresh' && food.persistent &&
		food.userId !== null && food.budgetId !== null;

	const save = () => {
		timer = null;
		const food = store.getState().food;
		if(!persistable(food)) {
			return Promise.resolve(false);
		}
		return environment().then((env) => {
			if(!env.persisted || !env.session || String(env.session.userId) !== String(food.userId)) {
				return false;
			}
			const record = {
				schema: FOOD_SCHEMA,
				version: food.version,
				savedAt: Math.floor(env.now() / 1000),
				syncedAt: food.lastSyncedAt,
				coverage: food.coverage,
				confirmed: food.confirmed,
				// The applied op results `confirmed` holds, so a reload does not fold them over it again.
				folded: Object.keys(food.folded).filter((id) => food.folded[id].status === 'applied'),
			};
			return env.persisted.save(foodKey(food.userId, food.budgetId), record, env.session)
				.then((saved) => (saved ?
					env.persisted.keepNewest(FOOD_NAMESPACE, FOOD_SCHEMA, food.userId, KEEP_BUDGETS)
						.then(() => store.dispatch(pruneFoldedResults(food.appliedSnapshotEpoch, food.loadId)))
						.then(() => true) :
					false));
		}).catch((error) => {
			console.error(error);
			return false;
		});
	};

	const unsubscribe = store.subscribe(() => {
		const food = store.getState().food;
		if(food.confirmed === lastConfirmed) {
			return;
		}
		lastConfirmed = food.confirmed;
		if(!persistable(food)) {
			return;
		}
		if(timer !== null) {
			cancel(timer);
		}
		timer = schedule(save, delay);
	});

	return {
		stop() {
			stopped = true;
			if(timer !== null) {
				cancel(timer);
				timer = null;
			}
			unsubscribe();
		},
		flush() {
			if(timer !== null) {
				cancel(timer);
			}
			return save();
		},
	};
}
